import { UsersMeOrganizationQuery } from '@/gql/systemApi'
import { ModalTypes } from '@/types'
import { TError } from '@/utils/api/types'
import { ReactNode } from 'react'
import { CaseEntry } from '../MyReferralsV2/types'
import { TMyReferralsFilters } from './Filters/types'

// V4 API Error Types
export interface ApiError {
  message: string
  code?: string
  details?: unknown
}

export interface ApiResponse<T> {
  data: T
  error?: ApiError
}

// V4 API Types
interface TabCounts {
  matched: number
  open: number
  inactive: number
  signed: number
  'needs-action': number
}

// Case status enums for API calls
export enum CaseStatus {
  OPEN = 'open',
  MATCHED = 'matched',
  SIGNED = 'signed',
}

// Category enums for API calls
export enum ApiCategory {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

// Tab name enums for API calls
export enum TabName {
  OPEN = 'open',
  MATCHED = 'matched',
  SIGNED = 'signed',
  INACTIVE = 'inactive',
  NEEDS_ACTION = 'needs-action',
}

// Separate needs action data interface
export interface NeedsActionData {
  openReferrals: CaseEntry[]
  matchedReferrals: CaseEntry[]
}

export interface MyReferralsCounts {
  inbound: TabCounts
  outbound: TabCounts
}

// Old V4 API response shape (wraps counts in a `data` property)
export interface MyReferralsCountsV4Response {
  data: {
    inbound: TabCounts
    outbound: TabCounts
  }
}

// Category-specific counts to support both inbound and outbound
export interface CategoryCounts {
  inbound: TabCounts | null
  outbound: TabCounts | null
}

// Metrics data interface for signed referrals overview
export interface SignedReferralsMetrics {
  signedReferrals: number
  updateComing: number
  closedCases: number
  openCases: number
  // Earnings values (ENG-1226) — raw numbers, FE formats them
  totalEarned?: number
  referringEarnings?: number
  receivingEarnings?: number
}

// Counterparts API Types
export interface CounterpartAttorney {
  id: string // This is the user_id
  attorney_id: string // The attorney entity ID
  name: string
}

export interface CounterpartLawFirm {
  id: string
  name: string
}

export interface CounterpartsResponse {
  counterparts: CounterpartAttorney[]
  counterpartLawFirms: CounterpartLawFirm[]
}

// V4 API Pagination Types
export interface PaginationInfo {
  currentPage: number
  perPage: number
  totalEntries: number
  totalPages: number
  nextPage: number | null
  prevPage: number | null
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: PaginationInfo
}

// Type for the detailed referrals endpoint response
export type PaginatedCaseEntriesResponse = PaginatedResponse<CaseEntry>

// Segmented Needs Action API Types (ENG-385)
export interface SegmentedPaginationInfo {
  currentPage: number
  perPage: number
  totalEntries: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export interface SegmentedNeedsActionSegment {
  data: CaseEntry[]
  pagination: SegmentedPaginationInfo
}

export interface SegmentedNeedsActionResponse {
  open: SegmentedNeedsActionSegment
  matched: SegmentedNeedsActionSegment
  signed: SegmentedNeedsActionSegment
}

export interface SegmentedPaginationParams {
  open_page: number
  open_limit: number
  matched_page: number
  matched_limit: number
  signed_page: number
  signed_limit: number
}

// Export types for use in other files
export type { TabCounts }

// Enhanced type definitions with better type safety
export interface TabDataPrecomputed {
  referralsThatNeedAttention: number
}

export interface LoadingStates {
  countsLoading: boolean
  contentLoading: boolean
  isLoading: boolean
  isRefetching: boolean
  metricsLoading: boolean
}

export interface ApiData {
  countsData?: MyReferralsCounts
  categoryCountsData?: CategoryCounts
  paginationData?: PaginationInfo
  metricsData?: SignedReferralsMetrics
}

export interface SelectionState {
  selectedReferralCategory: ReferralCategory
  setSelectedReferralCategory: (
    value: ReferralCategory | ((prev: ReferralCategory) => ReferralCategory)
  ) => void
  selectedTabValue: TabIndex
  setSelectedTabValue: (
    value: TabIndex | ((prev: TabIndex) => TabIndex)
  ) => void
}

// Map of filters per category for isolation
export type FiltersByCategory = {
  [key in ReferralCategory]: TMyReferralsFilters
}

export interface FilterState {
  filters: TMyReferralsFilters
  tempFilters: TMyReferralsFilters
  setFilters: React.Dispatch<React.SetStateAction<TMyReferralsFilters>>
  setTempFilters: React.Dispatch<React.SetStateAction<TMyReferralsFilters>>
}

export interface UnmatchTarget {
  caseId: string
  casePurchaseId: string
  buyerName: string
}

export interface IMyReferralsContext {
  error: TError | null
  counterpartsError: TError | null
  isLoading: boolean
  isRefetching: boolean
  myTasksEnabled: boolean
  refetch: () => Promise<void>
  needsYourAttentionData: CaseEntry[]
  responseModal: {
    type: ModalTypes
    open: boolean
  }
  /**
   * Represents all cases relevant to the user.
   */
  caseEntries: CaseEntry[]
  rawCaseEntries: CaseEntry[]
  getIsSameOrg: (orgId: string) => boolean
  watchedCases: CaseEntry[]
  organizationData: UsersMeOrganizationQuery | undefined
  // V4 API specific data
  countsData: MyReferralsCounts | null
  categoryCountsData?: CategoryCounts
  paginationData?: PaginationInfo
  metricsData: SignedReferralsMetrics | null
  counterpartsData: CounterpartsResponse | null
  // Separate needs action data
  needsActionData: NeedsActionData
  needsActionLoading: {
    open: boolean
    matched: boolean
  }
  // Separate loading states for better UX
  countsLoading: boolean
  contentLoading: boolean
  metricsLoading: boolean
  counterpartsLoading: boolean
  // Precomputed tab data to prevent multiple useTabData calls
  outboundTabData: TabDataPrecomputed
  inboundTabData: TabDataPrecomputed
  // Selection state
  selectedReferralCategory: ReferralCategory
  setSelectedReferralCategory: (
    value: ReferralCategory | ((prev: ReferralCategory) => ReferralCategory)
  ) => void
  selectedTabValue: TabIndex
  setSelectedTabValue: (
    value: TabIndex | ((prev: TabIndex) => TabIndex)
  ) => void
  // Signed referral sub-tab selection
  selectedSignedSubTab: SignedSubTab
  setSelectedSignedSubTab: (value: SignedSubTab) => void
  // Filter state - now category-specific
  filtersByCategory: FiltersByCategory
  tempFiltersByCategory: FiltersByCategory
  debouncedFiltersByCategory: FiltersByCategory
  // Convenience accessors for current category
  filters: TMyReferralsFilters
  tempFilters: TMyReferralsFilters
  debouncedFilters: TMyReferralsFilters
  setFilters: React.Dispatch<React.SetStateAction<TMyReferralsFilters>>
  setTempFilters: React.Dispatch<React.SetStateAction<TMyReferralsFilters>>
  // API functions for component-level queries
  fetchReferralDetails: (
    tabName: TabName,
    category?: ApiCategory,
    filters?: TMyReferralsFilters,
    page?: number,
    perPage?: number
  ) => Promise<PaginatedCaseEntriesResponse>
  fetchNeedsActionData: (
    caseStatus: CaseStatus,
    category?: ApiCategory,
    filters?: TMyReferralsFilters,
    page?: number,
    perPage?: number
  ) => Promise<PaginatedCaseEntriesResponse>
  fetchSegmentedNeedsActionData: (
    category: ApiCategory,
    paginationParams: SegmentedPaginationParams,
    filters?: TMyReferralsFilters
  ) => Promise<SegmentedNeedsActionResponse>
  fetchCounterparts: (tabName: TabName) => Promise<CounterpartsResponse>
  // Cache invalidation function for mutations
  invalidateAllQueries: () => Promise<void>
  // Comprehensive cache invalidation for all tabs (including inactive ones)
  invalidateAllTabCaches: () => Promise<void>
  // Direct data invalidation that bypasses React Query complexity
  invalidateAllDataDirect: () => void
  // Systematic invalidation and refetch
  invalidateAndRefetch: () => Promise<void>
  /** Unmatch & Repost (ENG-1709): the row the modal acts on, snapshotted on click. */
  unmatchTarget: UnmatchTarget | null
  setUnmatchTarget: (target: UnmatchTarget | null) => void
  // Active tab refetch registration (aligned with counts approach)
  registerActiveTabRefetch: (
    tabId: string,
    refetchFn: () => Promise<unknown>
  ) => void
  unregisterActiveTabRefetch: (tabId: string) => void
  fetchAndCacheMetrics: () => Promise<void>
  // Centralized tab data management
  fetchTabData: (
    tabName: TabName,
    category?: ApiCategory,
    caseStatus?: CaseStatus,
    page?: number,
    perPage?: number
  ) => Promise<PaginatedCaseEntriesResponse | null>
  getTabData: (
    tabName: TabName,
    category?: ApiCategory,
    caseStatus?: CaseStatus
  ) => {
    data: PaginatedCaseEntriesResponse | null
    loading: boolean
    error: TError | null
    lastFetch: number
  }
  // Helper functions
  getCategoryApiParam: (category: ReferralCategory) => ApiCategory
  getFilterApiParams: (filters: TMyReferralsFilters) => Record<string, unknown>
  // Jurisdiction data and mapping
  jurisdictionsData: Array<{
    __typename?: string
    id: string
    code?: string | null
    name?: string | null
  }>
  getJurisdictionCodeFromId: (jurisdictionId: string) => string | null
  // Page state management
  getPageState: (
    tabKey: string,
    category?: string,
    caseStatus?: string
  ) => { currentPage: number; rowsPerPage: number }
  updatePageState: (
    tabKey: string,
    updates: Partial<{ currentPage: number; rowsPerPage: number }>,
    category?: string,
    caseStatus?: string
  ) => void
  resetAllPageStates: () => void
  // Note: Removed loadedTabCounts - using real-time React Query cache aggregation
}

export enum ReferralCategory {
  MY_TASKS = 'MY_TASKS',
  OUTBOUND = 'OUTBOUND',
  INBOUND = 'INBOUND',
  SIGNED = 'SIGNED',
}

export enum TabIndex {
  NEEDS_ACTION = 0,
  OPEN_REFERRALS = 1,
  MATCHED = 2,
  INACTIVE = 3,
}

export enum SignedSubTab {
  ALL = 'ALL',
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

export interface TabData {
  id: TabIndex
  title: string
  caseEntries: CaseEntry[]
  header?: ReactNode
  bodyContent?: ReactNode
  disabled?: boolean
  numberOfCases?: number
}
